import React from 'react';

import { Stack, Text, Center, Paper, LoadingOverlay } from '@mantine/core';
import { Calendar } from '@mantine/dates';
import dayjs from 'dayjs';
import useSelfBookingAppointmentCoachAvailableDates from 'hooks/appointmentSelfBooking/useSelfBookingAppointmentCoachAvailableDates';

interface DateSelectionStepProps {
  appointmentTypeId: string;
  staffId: string;
  currentMonth: Date;
  selectedDate: Date | null;
  onMonthChange: (date: Date) => void;
  onDateSelect: (date: Date) => void;
}

const today = new Date();
const oneYearFromToday = dayjs().add(1, 'year').toDate();

const SelfBookingAppointmentDateSelectionStep = ({
  appointmentTypeId,
  staffId,
  currentMonth,
  selectedDate,
  onMonthChange,
  onDateSelect,
}: DateSelectionStepProps): JSX.Element => {
  const {
    data: coachAvailabilitiesDatesData,
    isSuccess: isCalendarDataSuccess,
    isLoading: isCoachAvailabilitiesDatesLoading,
  } = useSelfBookingAppointmentCoachAvailableDates({
    appointmentTypeId,
    staffId,
    month: dayjs(currentMonth).format('YYYY-MM-DD'),
    enabled: !!appointmentTypeId && !!staffId,
  });

  const availableDates = isCalendarDataSuccess
    ? coachAvailabilitiesDatesData?.data?.coachAvailableDates
    : undefined;

  return (
    <>
      <Stack spacing={0}>
        <Text ta='center' size='lg'>
          Select a date
        </Text>

        <Text ta='center' color='dimmed'>
          Below you can find the available dates for the selected coach
        </Text>
      </Stack>

      <Center mt='xl'>
        <Paper sx={{ position: 'relative' }}>
          <LoadingOverlay visible={isCoachAvailabilitiesDatesLoading} />

          <Calendar
            size='xl'
            placeholder={today}
            date={currentMonth}
            onDateChange={onMonthChange}
            onNextMonth={onMonthChange}
            onPreviousMonth={onMonthChange}
            onMonthSelect={onMonthChange}
            minDate={today}
            maxDate={oneYearFromToday}
            withCellSpacing={false}
            getDayProps={date => {
              const formattedDate = dayjs(date).format('YYYY-MM-DD');
              const isToday = dayjs(date).isSame(today, 'date');
              const isAfter = dayjs(date).isAfter(today);
              const isTodayOrAfterToday = isToday || isAfter;
              const isWithinOneYear =
                dayjs(date).isBefore(oneYearFromToday) || dayjs(date).isSame(oneYearFromToday, 'date');

              const isAvailable =
                availableDates && isTodayOrAfterToday && isWithinOneYear && availableDates[formattedDate];

              const isSelected = dayjs(date).isSame(selectedDate);

              return {
                disabled: !isAvailable,
                selected: isSelected,
                onClick: () => onDateSelect(date),
              };
            }}
          />
        </Paper>
      </Center>
    </>
  );
};

export default SelfBookingAppointmentDateSelectionStep;
